import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/**
 * Mirrors {@code FeedbackEntry} record from the backend. {@code screenId} is the
 * {@code pbWindow} string -- same identifier the help popup, queue page, and
 * {@code WINDOW_TO_ROUTE} lookups share.
 */
export interface FeedbackEntry {
  id: number;
  screenId: string;
  reviewerUserId: string;
  reviewerName: string | null;
  problemText: string;
  status: string;
  resolutionNotes: string | null;
  prUrl: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface FeedbackCountResponse {
  count: number;
}

export interface FeedbackSubmitRequest {
  screenId: string;
  problemText: string;
}

const BASE = '/api/feedback';

/** Build a {@code key} array so multiple filter combos cache independently. */
function countKey(screenId: string | undefined, status: string | undefined) {
  return ['feedback', 'count', { screenId: screenId ?? null, status: status ?? null }] as const;
}

function listKey(screenId: string | undefined, status: string | undefined) {
  return ['feedback', 'list', { screenId: screenId ?? null, status: status ?? null }] as const;
}

function qsParams(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && String(v).length > 0) sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * Fetch the open-issues count for a screen. Used by ScreenHelpButton to render
 * the "Open Issues: N" badge with conditional Link.
 *
 * <p>{@code enabled} defaults to true when {@code screenId} is provided; pass
 * {@code enabled: false} from a closed popover to skip the request entirely.
 */
export function useFeedbackCount(
  screenId: string | undefined,
  status: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: countKey(screenId, status),
    queryFn: () =>
      api<FeedbackCountResponse>(BASE + '/count' + qsParams({ screenId, status })),
    enabled: (options?.enabled ?? true) && (screenId == null || screenId.length > 0),
    staleTime: 30_000,
  });
}

/**
 * List open issues with optional filters. Used by the {@code /feedback/queue}
 * page (step 4). Newest first; capped at 100 by default by the backend.
 */
export function useFeedbackList(
  screenId: string | undefined,
  status: string | undefined,
) {
  return useQuery({
    queryKey: listKey(screenId, status),
    queryFn: () =>
      api<FeedbackEntry[]>(BASE + qsParams({ screenId, status })),
    staleTime: 10_000,
  });
}

/**
 * Submit a new feedback row. On success, invalidates every {@code ['feedback']}
 * query so the help-popup count + queue list both refresh without callers
 * needing to thread invalidation manually.
 */
export function useSubmitFeedback() {
  const qc = useQueryClient();
  return useMutation<FeedbackEntry, Error, FeedbackSubmitRequest>({
    mutationFn: (body) => api<FeedbackEntry>(BASE, { method: 'POST', json: body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['feedback'] });
    },
  });
}
